import '.la{height:17rem;}.ssr-plugin'
import '.b7{overflow:hidden;}.ssr-plugin'
import '.bi{border-radius:0.625rem;}.ssr-plugin'
import '.lb{max-height:17rem;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.s{flex-direction:row;}.ssr-plugin'
import '.d{gap:1rem;}.ssr-plugin'
import '.bj{position:absolute;}.ssr-plugin'
import '.lc{left:-9999px;}.ssr-plugin'
import '.h3{opacity:0;}.ssr-plugin'
import '.cp{pointer-events:none;}.ssr-plugin'
import '.bc{height:100%;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.aq{justify-content:center;}.ssr-plugin'
import '.bg{aspect-ratio:1/1;}.ssr-plugin'
import '.ld{color:#fff;}.ssr-plugin'
import '.a4{font-family:FunnelDisplay;}.ssr-plugin'
import '.cn{font-weight:700;}.ssr-plugin'
import '.a6{font-style:normal;}.ssr-plugin'
import '.le{font-size:3rem;}.ssr-plugin'
import '.lf{line-height:100%;}.ssr-plugin'
import '.a5{font-weight:400;}.ssr-plugin'
import '.ba{font-size:0.875rem;}.ssr-plugin'
import '.lg{margin-top:0.5rem;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import Row from '@app/components/layout/Row'
import { useState, useCallback } from 'react'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import { ImageDisplay } from './blocks/ImageDisplay'
import { ImageOveriew } from './blocks/ImageOverview'
import { ImagePreview } from './blocks/ImagePreview'

export type PostMediaGalleryProps = {
  images: string[]
  overviewRenderContainer?: HTMLElement
  clickable?: boolean
}

export const PostMediaGallery = ({
  images,
  overviewRenderContainer,
  clickable = true,
}: PostMediaGalleryProps) => {
  // Always call hooks first
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.mediaGallery
  const [displayedIndex, setDisplayedIndex] = useState<number>(null)
  const [overviewShown, setOverviewShown] = useState(false)

  const renderImagePreview = useCallback(
    (index: number, wrapperStyle?: Style) => {
      return (
        <ImagePreview
          image={images[index]}
          index={index}
          wrapperStyle={{
            minWidth: 0,
            ...wrapperStyle,
          }}
          imageStyle={{
            borderRadius: '0.625rem',
            maxWidth: '100%',
            cursor: clickable ? 'pointer' : 'default',
          }}
          onImageClick={clickable ? setDisplayedIndex : undefined}
        />
      )
    },
    [images, clickable],
  )

  const modalHeader = useCallback((onClose: () => void) => (
    <Row
      style={{
        flexDirection: 'row-reverse',
        alignItems: 'center',
      }}
    >
      <CloseButton onClick={onClose} />
    </Row>
  ), [])

  const renderOveriew = useCallback(() => {
    return (
      <ImageOveriew
        images={images}
        header={modalHeader(() => setOverviewShown(false))}
        onImageClick={setDisplayedIndex}
      />
    )
  }, [images, modalHeader])

  const closeModal = useCallback(() => {
    setDisplayedIndex(null)
    setOverviewShown(false)
  }, [])

  const goToOverview = useCallback(() => {
    setOverviewShown(true)
    setDisplayedIndex(null)
  }, [])

  const renderDisplay = useCallback(() => {
    return (
      <ImageDisplay
        displayedImage={images[displayedIndex]}
        overviewButtonLabel={translations.allMedia}
        header={modalHeader(() => setDisplayedIndex(null))}
        canGoBack={displayedIndex !== 0}
        canGoForwad={displayedIndex !== images.length - 1}
        goBack={() => setDisplayedIndex((prev) => prev - 1)}
        goForward={() => setDisplayedIndex((prev) => prev + 1)}
        goToOverview={images.length > 1 ? goToOverview : undefined}
      />
    )
  }, [images, displayedIndex, translations.allMedia, modalHeader, goToOverview])

  const renderModal = useCallback(() => {
    if (!clickable) {return null}
    if (displayedIndex === null && !overviewShown) {return null}
    return (
      <ModalWrapper
        renderContainer={overviewRenderContainer}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6.5px)',
          overflowY: 'scroll',
          alignItems: displayedIndex !== null ? 'center' : 'flex-start',
        }}
        onClose={closeModal}
      >
        {displayedIndex !== null ? renderDisplay() : renderOveriew()}
      </ModalWrapper>
    )
  }, [clickable, displayedIndex, overviewShown, renderDisplay, renderOveriew, overviewRenderContainer, closeModal])

  // Early return after all hooks
  if (images.length === 0) {return null}

  if (images.length === 1) {
    return (
      <>
        {renderModal()}
        <div className="la b7 bi"
        >
          {renderImagePreview(0, { borderRadius: '0.625rem' })}
        </div>
      </>
    )
  }

  if (images.length < 4) {
    return (
      <>
        {renderModal()}
        <div className="lb a s d"
        >
          {renderImagePreview(0, { aspectRatio: '1/1' })}
          {images.length > 1 && renderImagePreview(1, { aspectRatio: '1/1' })}
          {images.length > 2 && renderImagePreview(2, { aspectRatio: '1/1' })}
          {images.length > 2 && clickable && (
            <div className="bj lc h3 cp"
              onClick={() => setOverviewShown(true)}
            >
              {translations.allMedia}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {renderModal()}
      <div className="lb a s d"
      >
        {renderImagePreview(0, { aspectRatio: '1/1' })}
        {renderImagePreview(1, { aspectRatio: '1/1' })}
        {images.slice(2).map((_, index) => renderImagePreview(index + 2, { display: 'none' }))}
        <div className="bc n a b t aq bi bg"
          onClick={clickable ? () => setOverviewShown(true) : undefined}
          style={{
            cursor: clickable ? 'pointer' : 'default',
            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${images[2]}) lightgray 50% / cover no-repeat`,
          }}
        >
          <div className="ld a4 cn a6 le lf"
          >
            +{images.length - 2}
          </div>
          <div className="ld a4 a5 ba lg"
          >
            {translations.allMedia}
          </div>
        </div>
      </div>
    </>
  )
}
